import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ParticipantHistory as History, ParticipantVersion } from '@regigov/contracts';
import { ProblemAlert, StatusBadge, formatDateTime } from './ui';

interface Props {
  queryKey: unknown[];
  load: () => Promise<History>;
}

/**
 * Collapsed by default: summary and search views only ever show current state
 * (decision 6). Expanding shows every version with who changed it, why, when it
 * was true (valid_from/valid_to) and when CER recorded it.
 */
export function ParticipantHistoryPanel({ queryKey, load }: Props) {
  const [open, setOpen] = useState(false);
  const history = useQuery({ queryKey, queryFn: load, enabled: open });

  return (
    <details className="history" onToggle={(e) => setOpen(e.currentTarget.open)}>
      <summary>History</summary>
      {history.isLoading && <p className="muted">Loading history…</p>}
      <ProblemAlert error={history.error} />
      {history.data && (
        <table className="table">
          <thead>
            <tr>
              <th>Version</th>
              <th>ID</th>
              <th>Status</th>
              <th>True from</th>
              <th>True until</th>
              <th>Recorded</th>
              <th>Changed by</th>
              <th>Reason</th>
              <th>Changes</th>
            </tr>
          </thead>
          <tbody>
            {history.data.versions.map((v, i, all) => (
              <tr key={v.versionId}>
                <td>{v.version}</td>
                <td>
                  <code>#{v.versionId}</code>
                  {v.correctionOf !== null && <span className="muted"> corrects #{v.correctionOf}</span>}
                </td>
                <td>
                  <StatusBadge status={v.data.status} />
                </td>
                <td>{formatDateTime(v.validFrom)}</td>
                <td>
                  {v.supersededBy !== null ? (
                    <em className="muted">superseded by #{v.supersededBy}</em>
                  ) : v.validTo ? (
                    formatDateTime(v.validTo)
                  ) : (
                    <em className="muted">current</em>
                  )}
                </td>
                <td>{formatDateTime(v.recordedAt)}</td>
                <td>
                  {v.changedByName ?? <span className="muted">{v.sourceChannel}</span>}
                </td>
                <td>{v.changeReason}</td>
                <td>{describeDiff(all[i + 1], v)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </details>
  );
}

function describeDiff(previous: ParticipantVersion | undefined, current: ParticipantVersion): string {
  if (!previous) return 'Created';
  const changed: string[] = [];
  const a = previous.data as Record<string, unknown>;
  const b = current.data as Record<string, unknown>;
  for (const key of Object.keys(b)) {
    if (JSON.stringify(a[key]) !== JSON.stringify(b[key])) changed.push(key);
  }
  return changed.length ? changed.join(', ') : 'No field changes';
}
